/* SPEC-509 — the rail's foot cluster for PHONES. The rail is desktop-only (`md:`), so the
 * overlay menu ends with the same three actions as labelled rows: Alerts (with the same ONE
 * badge), Walk-throughs, Feedback. Same rules as the rail (`config/shell.railFootItems`,
 * `alertsBadge`, `alertsLabel`), so the two can never disagree. */
import { NavLink } from 'react-router-dom';
import { RailMark } from '../brand/marks/Mark';
import { useTour } from '../tour/TourProvider';
import { useAlertsFeeds } from '@/hooks/useAlertsFeeds';
import { railFootItems, alertsBadge, alertsLabel, openFeedback } from '@/config/shell';
import { AlertsBadgeMark } from './AppRail';
// SPEC-535: the phone rows sit beside the page rows in the same menu, so they take the page
// tier of the one nav scale rather than a size of their own.
import { NAV_SCALE } from '@/config/nav-scale';

const ROW_BASE =
  `w-full flex items-center gap-3 px-2 py-2 min-h-[44px] rounded-lg ${NAV_SCALE.page.text} ` +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60 transition-colors';
// Colour classes are chosen, never stacked: two text colours on one element resolve by CSS
// order, not class order.
const ROW_IDLE =
  'text-[var(--text-secondary)] [--mark-accent:currentColor] hover:text-[var(--text-primary)] hover:bg-[var(--sidebar-hover)]';
const ROW_ACTIVE = 'text-amber-400 [--mark-accent:var(--amber-400)] bg-[var(--sidebar-active-bg)]';
const ROW = `${ROW_BASE} ${ROW_IDLE}`;

export function RailFootRows({ pages, onNavigate }: { pages: readonly string[]; onNavigate: () => void }) {
  const { openPicker } = useTour();
  const items = railFootItems(pages);
  const feeds = useAlertsFeeds(items.includes('alerts'));
  const badge = alertsBadge(feeds);

  return (
    <div className="mb-3 space-y-0.5">
      {items.map((id) => {
        if (id === 'alerts') {
          return (
            <NavLink
              key={id}
              to="/alerts"
              onClick={onNavigate}
              aria-label={alertsLabel(feeds)}
              className={({ isActive }) => `${ROW_BASE} ${isActive ? ROW_ACTIVE : ROW_IDLE}`}
            >
              <RailMark id="alerts" />
              <span className="flex-1 text-left">Alerts</span>
              <AlertsBadgeMark badge={badge} className="relative" />
            </NavLink>
          );
        }
        if (id === 'tours') {
          return (
            <button
              key={id}
              type="button"
              aria-haspopup="dialog"
              // Close the menu first, so the picker is not opened under the overlay.
              onClick={() => { onNavigate(); openPicker(); }}
              className={ROW}
            >
              <RailMark id="tours" />
              <span className="flex-1 text-left">Guided walk-throughs</span>
            </button>
          );
        }
        return (
          <button
            key={id}
            type="button"
            onClick={() => { onNavigate(); openFeedback(window, 'rail'); }}
            className={ROW}
          >
            <RailMark id="feedback" />
            <span className="flex-1 text-left">Feedback</span>
          </button>
        );
      })}
    </div>
  );
}
